/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2019. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React from 'react';
import { Switch, Route } from 'react-router-dom';
import { Routes as UARoutes, TitledRoute, AppSpinner } from '@spm/universal-access-ui';
import { AuthenticatedRoute } from '@spm/core-ui';
import { Authentication } from '@spm/universal-access';
import Loadable from 'react-loadable';
import PATHS from '@spm/universal-access-ui/src/router/Paths';
import routeTranslations from '@spm/universal-access-ui/src/router/RoutesMessages';
import routeTitles from './routesMessages';
import SDPATHS from './paths';
import applicationHeaderTranslations from './features/ApplicationHeader/components/ApplicationHeaderMessages';
import applicationOverviewTranslations from './features/ApplicationOverview/components/ApplicationOverviewComponentMessages';
import surveyTranslations from './features/Survey/components/SurveyComponentMessages';
import applyForBenfitsTranslations from './features/ApplyForBenefits/components/ApplyForBenefitsComponentMessages';
import signupTranslations from './features/Signup/components/SignupComponentMessages';
import yourBenefitsTranslations from './features/YourBenefits/components/YourBenefitsComponentMessages';
import linkAccountTranslations from './features/LinkAccount/components/LinkAccountComponentMessages';
import onlineCOCOverviewTranslations from './features/OnlineCOCOverview/components/OnlineCOCOverviewComponentMessages';
import renewalApplicationOverviewTranslations from './features/RenewalApplicationOverview/components/RenewalApplicationOverviewComponentMessages';
import SDIDProofingTranslations from './features/SDIDProofing/components/sdPrimaryRIDPComponent/SDIDProofingComponentMessages';
import SDIDProofingResultTranslations from './features/SDIDProofing/components/sdSecondaryRIDPComponent/SDIDProofingResultComponentMessages';
import SDFarsResultTranslations from './features/SDIDProofing/components/sdFarsComponent/SDFARSResultComponentMessages';
import BenefitSelectionTranslations from './features/BenefitSelection/components/BenefitSelectionComponentMessages';
import sdccaRenewalApplicationOverviewTranslations from './features/SDCCARenewalApplicationOverview/components/SDCCARenewalApplicationOverviewComponentMessages';
import SNAPApplicationTranslations from './features/SNAPApplication/components/SNAPApplicationComponentMessages';

/*  This routes.jsx file can be customized to add new components or
    Redirect the existing components to new routes:

   Sample:
     <Switch>
       <Route path="/ourPayments" component={OurPayments} />
       <Redirect from="/payments" to="/ourPayments" />
       <UARoutes />
     </Switch>
*/

/**
 * Default loading component to display while loading other components.
 */
const loadingComponent = () => <AppSpinner />;

const Organisation = Loadable({
  loader: () => import(/* webpackChunkName: "Organisation" */ './features/Organisation'),
  loading: loadingComponent,
});

/* BEIGN SD-1050 YP: Adding constant info for FAQ page */
const FAQ = Loadable({
  loader: () => import(/* webpackChunkName: "FAQ" */ './features/FAQ'),
  loading: loadingComponent,
});
/* END SD-1050 YP: Adding constant info for FAQ page */

/* BEIGN SD-1051 YP: Adding constant info for Contact us page */
const ContactUs = Loadable({
  loader: () => import(/* webpackChunkName: "ContactUS" */ './features/ContactUs'),
  loading: loadingComponent,
});
/* END SD-1050 YP: Adding constant info for ContactUS page */

const SampleApplication = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "SampleApplication" */ './features/sampleApplication/SampleApplicationComponent'
    ),
  loading: loadingComponent,
});
const SampleApplicationForm = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "SampleApplicationForm" */ './features/sampleApplication/form/SampleApplicationFormComponent'
    ),
  loading: loadingComponent,
});
const SampleApplicationOverview = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "SampleApplicationOverview" */ './features/sampleApplication/overview/SampleApplicationOverviewComponent'
    ),
  loading: loadingComponent,
});
const SampleApplicationConfirmation = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "SampleApplicationConfirmation" */ './features/sampleApplication/confirmation/SampleApplicationConfirmation'
    ),
  loading: loadingComponent,
});

const AccountHome = Loadable({
  loader: () =>
    import(/* webpackChunkName: "SampleApplicationConfirmation" */ './features/AccountHome'),
  loading: loadingComponent,
});

/* BEGIN - SD-1064 - YP - Adding loadable for Profile */
const Profile = Loadable({
  loader: () => import(/* webpackChunkName: "Profile" */ './features/Profile'),
  loading: loadingComponent,
});

/* END - SD-1064 - YP - Adding loadable for Profile */
// SD-1908 - Staten - Adding routes while doing 4.1.4 upgrade
const ApplicationOverview = Loadable({
  loader: () =>
    import(/* webpackChunkName: "ApplicationOverview" */ './features/ApplicationOverview'),
  loading: loadingComponent,
});

// const LanguageDropdown = Loadable({
// loader: () => import(/* webpackChunkName: "LanguageDropdown" */ './features/LanguageDropdown'),
// loading: loadingComponent,
// });
// SD-1908 - Staten - Adding routes while doing 4.1.4 upgrade

/* BEGIN - SD-1066 - YP - Adding loadable for Local Office */
const LocalOffice = Loadable({
  loader: () => import(/* webpackChunkName: "LocalOffice" */ './features/LocalOffice'),
  loading: loadingComponent,
});
/* END - SD-1066 - YP - Adding loadable for Local Office */

/* BEGIN - SD-1067 - RC - Adding loadable for Submission Form. */
const SubmissionForm = Loadable({
  loader: () => import(/* webpackChunkName: "SubmissionForm" */ './features/Forms/SubmissionForm'),
  loading: loadingComponent,
});

const SubmissionFormConfirmation = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "SubmissionFormConfirmation" */ './features/Forms/SubmissionForm/SubmissionFormConfirmationComponent'
    ),
  loading: loadingComponent,
});
/* END - SD-1067 - RC - Adding loadable for Submission Form. */

/* BEGIN - SD-1067 - RC - Adding loadable for Survey. */
const Survey = Loadable({
  loader: () => import(/* webpackChunkName: "Survey" */ './features/Survey'),
  loading: loadingComponent,
});
/* END - SD-1067 - RC - Adding loadable for Survey. */

/* BEGIN - SD-1067 - RC - Adding Apply For Benefits Component */
const ApplyForBenefits = Loadable({
  loader: () => import(/* webpackChunkName: "ApplyForBenefits" */ './features/ApplyForBenefits'),
  loading: loadingComponent,
});
/* END - SD-1067 - RC - Adding Apply For Benefits Component */

/* BEGIN - SD-1919 - Staten - Adding Signup feature from OOTB to custom */
const Signup = Loadable({
  loader: () => import(/* webpackChunkName: "Signup" */ './features/Signup'),
  loading: loadingComponent,
});
/* END - SD-1919 - Staten - Adding Signup feature from OOTB to custom */

/* BEGIN - Staten - Adding YourBenefits feature to custom */
const YourBenefits = Loadable({
  loader: () => import(/* webpackChunkName: "YourBenefits" */ './features/YourBenefits'),
  loading: loadingComponent,
});
/* END - Staten - Adding YourBenefits feature to custom */
/* BEGIN - MS - SD-1054 - Adding YourSettings to custom */
const YourSettings = Loadable({
  loader: () => import(/* webpackChunkName: "YourSettings" */ './features/SDYourSettings'),
  loading: loadingComponent,
});
/* END - MS - SD-1054 - Adding YourSettings to custom */
/* BEGIN - MS - SD-1054 - Adding loadable for Link my account component. */
const LinkAccount = Loadable({
  loader: () => import(/* webpackChunkName: "LinkAccount" */ './features/LinkAccount'),
  loading: loadingComponent,
});
/* END - MS - SD-1054 - Adding loadable for Link my account component. */

/* BEGIN SD-1064 - Adding loadable for Online COC Componment */
const OnlineCOCOverview = Loadable({
  loader: () => import(/* webpackChunkName: "OnlineCOCOverview" */ './features/OnlineCOCOverview'),
  loading: loadingComponent,
});
/* END SD-1064 - Adding loadable for Online COC Componment  */

/* BEGIN SD-1064 - Adding loadable for Renewal Application Overview Componment */
const RenewalApplicationOverview = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "RenewalApplicationOverview" */ './features/RenewalApplicationOverview'
    ),
  loading: loadingComponent,
});
/* END SD-1064 - Adding loadable for Renewal Application Overview Componment  */

/* BEGIN - SD-1064 - AK - Loadable for application form component */
const ApplicationForm = Loadable({
  loader: () =>
    import(/* webpackChunkName: "ApplicationForm" */ './features/Forms/ApplicationForm'),
  loading: loadingComponent,
});
/* END - SD-1064 - AK - Loadable for application form component */
/* BEGIN - SD-1043 - Staten - Loadable for Verification Code component */
const SDVerificationCode = Loadable({
  loader: () =>
    import(/* webpackChunkName: "SDVerificationCode" */ './features/SDVerificationCode'),
  loading: loadingComponent,
});
/* END - SD-1043 - Staten - Loadable for Verification Code component  */

/* BEGIN - SD-1029 - MS - Loadable for RIDP components */
const SDIDProofing = Loadable({
  loader: () => import(/* webpackChunkName: "SDIDProofing" */ './features/SDIDProofing'),
  loading: loadingComponent,
});

const SDIDProofingResult = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "SDIDProofing" */ './features/SDIDProofing/components/sdSecondaryRIDPComponent/SDIDProofingResultComponent'
    ),
  loading: loadingComponent,
});

const SDFARSResult = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "SDIDProofing" */ './features/SDIDProofing/components/sdFarsComponent/SDFARSResultComponent'
    ),
  loading: loadingComponent,
});
/* END - SD-1029 - MS - Loadable for RIDP components */

// BEGIN - SD-6336 - MS - Modifying login page to force mySD redirect
const Login = Loadable({
  // BEGIN - DON'T CHECK THIS FILE IN WITH THIS LINE UNCOMMENTED.
  
  // loader: () => import('@spm/universal-access-ui/lib/features/Login'),

  // END - DON'T CHECK THIS FILE IN WITH THIS LINE UNCOMMENTED.
  
  // BEGIN - THIS LINE SHOULD BE UNCOMMENTED WHEN CHECKING THIS FILE INTO THE REPO
  loader: () => import('./features/Login'),
  // END - THIS LINE SHOULD BE UNCOMMENTED WHEN CHECKING THIS FILE INTO THE REPO
  
  loading: loadingComponent,
});

// BEGIN - SD-6337 - Pointing to modified eligibility page
const EligibilityHome = Loadable({
  loader: () => import(/* webpackChunkName: "EligibilityHome" */ './features/EligibilityHome'),
  loading: loadingComponent,
});
const EligibilityForm = Loadable({
  loader: () => import(/* webpackChunkName: "Eligibility" */ './features/Forms/Eligibility'),
  loading: loadingComponent,
});
// END - SD-6337 - Pointing to modified eligibility page

/** START SD-6522 - AK - adding route for BenefitSelection */
const BenefitSelection = Loadable({
  loader: () => import(/* webpackChunkName: "BenefitSelection" */ './features/BenefitSelection'),
  loading: loadingComponent,
});
/** END SD-6522 - AK - adding route for BenefitSelection */
/** BEGIN - Adding Verifications Component */
const VerificationDetails = Loadable({
  loader: () => import('./features/VerificationDetail'),
  loading: loadingComponent,
});
const Verifications = Loadable({
  loader: () => import('./features/Verifications'),
  loading: loadingComponent,
});
/** END - Adding Verifications Component */
/** BEGIN SD-8083 - Adding Notices Tab */
const Notices = Loadable({
  loader: () => import('./features/Notices'),
  loading: loadingComponent,
});
/** END SD-8083 - Adding Notices Tab */

/** BEGIN - SD-10416 - MS - Adding Notice Details Component */
const NoticeDetails = Loadable({
  loader: () => import('./features/NoticeDetails'),
  loading: loadingComponent,
});
/** END - SD-10416 - MS - Adding Notice Details Component */

const SDCCARenewalApplicationOverview = Loadable({
  loader: () =>
    import('./features/SDCCARenewalApplicationOverview'),
  loading: loadingComponent,
});

const SDCCAPayments = Loadable({
  loader: () =>
    import('./features/SDCCAPayments'),
  loading: loadingComponent,
});

const SDCCAPaymentsDetails = Loadable({
  loader: () =>
    import('./features/SDCCAPaymentsDetails'),
  loading: loadingComponent,
});
/** BEGIN SDMO-5859 CR-53 - CP - for SNAP-Only changes */
const SNAPApplication = Loadable({
   loader: () => import(/* webpackChunkName: "SNAPApplication" */ './features/SNAPApplication'),
  loading: loadingComponent,
});

const SNAPApplicationForm = Loadable({
  loader: () =>
    import(/* webpackChunkName: "SNAPApplicationForm" */ './features/Forms/SNAPApplicationForm'),
  loading: loadingComponent,
});
/** END SDMO-5859 CR-53 - CP - for SNAP-Only changes */

export default (
  <>
    <Switch>
      {/* Sample Routes - Example features. Not intended to run in production. */}
      <TitledRoute
        component={Organisation}
        exact
        path={PATHS.HOME}
        title={applicationHeaderTranslations.headerLandingPageTitle}
      />
      <TitledRoute
        component={SampleApplicationForm}
        exact
        path={`${SDPATHS.SAMPLE_APPLICATION.FORM}/:formId`}
        title={routeTitles.sampleApplicationForm}
      />
      <TitledRoute
        component={SampleApplicationConfirmation}
        exact
        path={`${SDPATHS.SAMPLE_APPLICATION.CONFIRMATION}/:formId`}
        title={routeTitles.sampleApplicationConfirmation}
      />
      <TitledRoute
        component={SampleApplicationOverview}
        exact
        path={SDPATHS.SAMPLE_APPLICATION.OVERVIEW}
        title={routeTitles.sampleApplicationOverview}
      />
      <TitledRoute
        component={SampleApplication}
        exact
        path={SDPATHS.SAMPLE_APPLICATION.ROOT}
        title={routeTitles.sampleApplication}
      />
      {/* BEGIN - SD-1067 - RC - Application Overview */}
      <TitledRoute
        component={ApplicationOverview}
        exact
        path={PATHS.APPLICATION_OVERVIEW}
        title={applicationOverviewTranslations.overviewTitle}
      />
      <TitledRoute
        component={ApplicationOverview}
        exact
        path={`${PATHS.APPLICATION_OVERVIEW}/:intakeApplicationTypeId/:programIds`}
        title={applicationOverviewTranslations.overviewTitle}
      />
      {/* END - SD-1067 - RC - Application Overview */}
      {/* BEGIN - SD-1064 - AK - adding route for Renewal Application Overview Page */}
      <TitledRoute
        component={RenewalApplicationOverview}
        exact
        path={SDPATHS.RENEWALAPPLICAATIONOVERVIEW}
        title={renewalApplicationOverviewTranslations.renewalApplicationOverviewPageTitle}
      />
      <TitledRoute
        component={RenewalApplicationOverview}
        exact
        path={`${SDPATHS.APPLICATION_OVERVIEW}/:./features/renewalApplicationOverview`}
        title={renewalApplicationOverviewTranslations.overviewTitle}
      />
      {/* END - SD-1064 - AK - adding route for Renewal Application Overview Page */}
      {/* BEGIN - SD-1067 - RC - Adding route for Submission. */}
      <TitledRoute
        component={SubmissionForm}
        exact
        isIEG
        path={`${PATHS.APPLY}/:submissionFormId/submit`}
        title={routeTranslations.submissionForm}
      />
      <TitledRoute
        component={SubmissionForm}
        exact
        isIEG
        path={`${PATHS.APPLY}/new/:applicationFormId/submit`}
        title={routeTranslations.submissionForm}
      />
      <TitledRoute
        component={SubmissionFormConfirmation}
        exact
        path={`${PATHS.APPLY}/:applicationFormId/confirm`}
        title={routeTranslations.submissionFormConfirmation}
      />
      {/* BEGIN - SD-1067 - RC - Adding route for Submission. */}
      {/* BEGIN - SD-1067 - RC - Adding route for Survey. */}
      <TitledRoute
        component={Survey}
        exact
        path={SDPATHS.SURVEY}
        title={surveyTranslations.title}
      />
       {/* END - SD-1067 - RC - Adding route for Survey. */}
       {/* BEGIN - SDMO-5859 CR-53 - CP - for SNAP-Only changes */}
      <TitledRoute
        component={SNAPApplication}
        exact
        path={SDPATHS.APPLYFORSNAP}
        title={SNAPApplicationTranslations.SNAP_Title}
      />   
       <TitledRoute
          component={SNAPApplicationForm}
          isIEG
          path={`${SDPATHS.APPLYFORSNAPFORM}/:applicationTypeId`}
          title={SNAPApplicationTranslations.SNAPFORM_Title}
        />
       <TitledRoute
          component={SNAPApplicationForm}
          isIEG
          //path={SDPATHS.APPLYFORSNAPFORM}
          path={`${SDPATHS.APPLYFORSNAPFORMRESUME}/:applicationFormId`}
          title={SNAPApplicationTranslations.SNAP_Title}
        />
        {/* END - SDMO-5859 CR-53 - CP - for SNAP-Only changes */}
      {/* BEGIN - SD-1067 - RC - Adding route for Apply For benefits. */}
      <AuthenticatedRoute
        authUserTypes={[Authentication.USER_TYPES.LINKED, Authentication.USER_TYPES.STANDARD]}
        component={ApplyForBenefits}
        exact
        path={PATHS.APPLY}
        title={applyForBenfitsTranslations.applyForBenefitsPageTitle}
      />
      {/* BEGIN - SD-1067 - RC - Adding route for Apply For Benefits. */}
      {/* BEGIN - SD-1064 - AK - Adding route for Apply For Application Form. */}
      <TitledRoute
        component={ApplicationForm}
        exact
        isIEG
        path={`${PATHS.APPLY}/new/:applicationTypeId`}
        title={routeTranslations.applicationForm}
      />
      <TitledRoute
        component={ApplicationForm}
        exact
        isIEG
        path={`${PATHS.APPLY}/:applicationFormId`}
        title={routeTranslations.applicationForm}
      />
      {/* END - SD-1064 - AK - Adding route for Application Form. */}
      {/* BEGIN - SD-1919 - Staten - Adding route for Signup feature */}
      <TitledRoute component={Signup} exact path={PATHS.SIGNUP} title={signupTranslations.title} />
      {/* END - SD-1919 - Staten - Adding route for Signup feature */}
      {/* BEGIN - Staten - Adding YourBenefits feature to custom */}
      <TitledRoute
        component={YourBenefits}
        exact
        path={PATHS.BENEFITS}
        title={yourBenefitsTranslations.title}
      />
      {/* END - Staten - Adding YourBenefits feature to custom */}
      {
        <AuthenticatedRoute
          authUserTypes={[Authentication.USER_TYPES.LINKED, Authentication.USER_TYPES.STANDARD]}
          component={AccountHome}
          exact
          path={PATHS.ACCOUNT}
          title={applicationHeaderTranslations.headerLandingPageTitle}
        />
      }
      {/* BEGIN - PR-11177 - JH - Adding route for Your Profile. */}
      <AuthenticatedRoute
        authUserTypes={[Authentication.USER_TYPES.STANDARD, Authentication.USER_TYPES.LINKED]}
        component={Profile}
        exact
        path={SDPATHS.PROFILE}
        title={applicationHeaderTranslations.headerProfileText}
      />
      {/* END - PR-11177 - JH - Adding route for Your Profile. */}
      {/* BEGIN - MS - SD-1054 - Adding route for Link Account. */}
      <TitledRoute
        component={LinkAccount}
        exact
        path={SDPATHS.LINK_ACCOUNT}
        title={linkAccountTranslations.linkAccountPageTitle}
      />
      {/* END - MS - SD-1054 - Adding route for Link Account. */}
      {/* BEGIN - MS - SD-1054 - Adding route for custom SDYourSettings page */}
      <Route component={YourSettings} path="/settings" />
      {/* END - MS - SD-1054 - Adding route for custom SDYourSettings page */}
      {/* BEGIN - PR-12447 - SD-1064 - Adding route for Online COC Overview Page. */}
      <AuthenticatedRoute
        authUserTypes={[Authentication.USER_TYPES.STANDARD, Authentication.USER_TYPES.LINKED]}
        component={OnlineCOCOverview}
        exact
        path={SDPATHS.ONLINECOCOVERVIEW}
        title={onlineCOCOverviewTranslations.onlineCOCOverviewPageTitle}
      />
      {/* END -PR-12447 - SD-1064 - Adding route for Online COC Overview Page.  */}
      {/* BEGIN - SD-1029 - MS - Verify your identity - RIDP  */}
      <TitledRoute
        component={SDIDProofing}
        exact
        path={SDPATHS.VERIFYYOURIDENTITY}
        title={SDIDProofingTranslations.title}
      />
      <TitledRoute
        component={SDIDProofingResult}
        exact
        path={SDPATHS.VERIFYYOURIDENTITYRESULT}
        title={SDIDProofingResultTranslations.title}
      />
      <TitledRoute
        component={SDFARSResult}
        exact
        path={SDPATHS.FARSRESULT}
        title={SDFarsResultTranslations.title}
      />
      {/* END - SD-1029 - MS - Verify your identity - RIDP  */}
      {/** Point to modified login page in deployment environment. On local, use the OOTB login page to bypass login/signup */}
      <TitledRoute
        component={Login}
        exact
        path={PATHS.LOGIN}
        title={routeTranslations.siteTitle}
      />
      {/* BEGIN - SD-6337 - Pointing to modified eligibility page */}
      <TitledRoute
        component={EligibilityForm}
        isIEG
        path={PATHS.ELIGIBILITY.FORM}
        title={routeTranslations.eligibilityFormContainerLabel}
      />
      <TitledRoute
        component={EligibilityForm}
        exact
        isIEG
        path={`${PATHS.ELIGIBILITY.RESUME}/:formId`}
        title={routeTranslations.eligibilityFormContainerResumeLabel}
      />
      <TitledRoute
        component={EligibilityHome}
        exact
        path={PATHS.ELIGIBILITY.ROOT}
        title={routeTranslations.eligibilityHomeLabel}
      />
      {/* END - SD-6337 - Pointing to modified eligibility page */}
      {/* BEGIN - SD-6522 - AK - adding route for Benefit Selection */}
      <AuthenticatedRoute
        authUserTypes={[Authentication.USER_TYPES.STANDARD, Authentication.USER_TYPES.LINKED]}
        component={BenefitSelection}
        exact
        path={PATHS.BENEFIT_SELECTION}
        title={BenefitSelectionTranslations.includeBenefitsTitle}
      />
      {/* END - SD-6522 - AK - adding route for Benefit Selection */}
      {/** BEGIN - Adding Verifications Component */}
      <AuthenticatedRoute
        authUserTypes={[Authentication.USER_TYPES.STANDARD, Authentication.USER_TYPES.LINKED]}
        component={Verifications}
        exact
        path={PATHS.VERIFICATIONS.ROOT}
        title={applicationHeaderTranslations.headerYourVerificationsText}
    />
      <TitledRoute
        component={VerificationDetails}
        exact
        path={PATHS.VERIFICATIONS.DETAILS}
        title={applicationHeaderTranslations.headerYourVerificationsText}
      />
      {/** END - Adding Verifications Component */}
      {/** BEGIN - SD-8083 - MS - Adding Notifications Component */}
      <TitledRoute
        component={Notices}
        exact
        path={PATHS.NOTICES.ROOT}
        title={applicationHeaderTranslations.headerNoticesLabel}
      />
       {/** END - SD-8083 - MS - Adding Notifications Component */}

      {/* <TitledRoute
        component={SDCCARenewalApplicationOverview}
        exact
        path={`${SDPATHS.SDCCARENEWALAPPLICAATIONOVERVIEW}/:./features/SDCCARenewalApplicationOverview`}
        title={sdccaRenewalApplicationOverviewTranslations.overviewTitle}
      /> */}
      {/** BEGIN - SD-10416 - MS - Adding Notice Details Component */}
      <Route
        component={NoticeDetails}
        exact
        path={PATHS.NOTICES.DETAILS}
      />
       {/** END - SD-10416 - MS - Adding Notice Details Component */}
      {/* UA Routes */}
      <Route component={FAQ} exact path={PATHS.FAQS} />
      <Route component={ContactUs} exact path={PATHS.CONTACT_US} />
      {/* BEGIN - SD-1066 - YP - Adding route for Your Local Offcie. */}
      <Route component={LocalOffice} exact path={SDPATHS.LOCAL_OFFICE} />
      {/* END - SD-1066 - YP - Adding route for Your Local OFfice. */}
      {/* BEGIN - SD-1043 - Staten - Route for Verification Code component */}
      <Route component={SDVerificationCode} path="/verificationCode" />
      {/* END - SD-1043 - Staten - Route for Verification Code component */}
      <Route component={SDCCARenewalApplicationOverview} path={SDPATHS.SDCCARENEWALAPPLICAATIONOVERVIEW} />
      <Route component={SDCCAPayments} path={SDPATHS.SDCCAPAYMENTS} />
      <Route component={SDCCAPaymentsDetails} path={SDPATHS.SDCCAPAYMENTSDETAILS} />
      <UARoutes />
    </Switch>
  </>
);
